import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import Table from "../../../core/common/dataTable";
import CollapseHeader from "../../../core/common/collapse-header/collapse-header";
import useDesignations from "../../../core/api/useDesignations";
import { all_routes } from "../../router/all_routes";
import useDepartment from "../../../core/api/useDepartment";
import { Modal } from "antd";
import { useAuthToken } from "../../../core/data/redux/useAuthToken";
import { usePermission } from "../../../core/api/useRolePermission";

const DesignationSchema = Yup.object().shape({
  designation_name: Yup.string().required("Designation name is required"),
  department_name: Yup.string().required("Department name is required"),
  status: Yup.string().required("Status is required"),
});

const Designations = () => {
  const empToken = useAuthToken();

  const {
    fetchDesignationsAllInfo,
    addDesignation,
    updateDesignation,
    deleteDesignation,
  } = useDesignations(empToken);

  const [isDeptLoading, setIsDeptLoading] = useState<boolean>(false);
  const [selectedDesignation, setSelectedDesignation] = useState<any | null>(null);
  const [departments, setDepartments] = useState<any[]>([]);
  const [allInfo, setAllInfo] = useState<any[]>([]);
  const [tableKey, setTableKey] = useState<number>(0);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const { fetchDepartmentNames } = useDepartment(empToken);

  // Permissions
  const canCreate = usePermission("Designations", "create");
  const canUpdate = usePermission("Designations", "write");
  const canDelete = usePermission("Designations", "delete");
  const canRead = usePermission("Designations", "read");

  useEffect(() => {
    fetchAllDesignationsInfo();
    fetchDepartments();
  }, []);

  const fetchAllDesignationsInfo = async () => {
    try {
      const data = await fetchDesignationsAllInfo();
      setAllInfo(data);
      setTableKey((prev) => prev + 1);
    } catch (err) {
      console.error("[Fetch All Designations Info] Error:", err);
    }
  };

  const fetchDepartments = async () => {
    setIsDeptLoading(true);
    try {
      const deptNames: string[] = await fetchDepartmentNames();
      const normalizedDepartments = deptNames.map((name: string) => ({
        value: name,
        label: name,
      }));
      setDepartments(normalizedDepartments);
    } catch (err) {
      console.error("[Fetch Department Names] Error:", err);
    } finally {
      setIsDeptLoading(false);
    }
  };

  const handleAdd = async (values: any, { setSubmitting, resetForm }: any) => {
    try {
      await addDesignation(values);
      toast.success("Designation added successfully");
      resetForm();
      document.getElementById("add_designation_close")?.click();
      await fetchAllDesignationsInfo();
    } catch (err) {
      console.error("Failed to add designation");
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = async (values: any, { setSubmitting, resetForm }: any) => {
    try {
      if (selectedDesignation?.designation) {
        const payload = {
          designation_name: values.designation_name,
          department_name: values.department_name,
          status: values.status,
        };

        await updateDesignation(selectedDesignation.designation, payload);
        toast.success("Designation updated successfully");
        resetForm();
        document.getElementById("edit_designation_close")?.click();
        await fetchAllDesignationsInfo();
        setSelectedDesignation(null);
      }
    } catch (err: any) {
      if (
        err?.response?.data?.code === "DATABASE_ERROR" &&
        err?.response?.data?.details?.includes(
          "Cannot delete or update a parent row"
        )
      ) {
        toast.warning(
          "Designation cannot be edited because it is assigned to one or more employees."
        );
      } else {
        console.error("Failed to update designation");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const confirmDelete = async () => {
    try {
      if (selectedDesignation?.department && selectedDesignation?.designation) {
        await deleteDesignation(
          selectedDesignation.department,
          selectedDesignation.designation
        );
        toast.success("Designation deleted successfully");
        await fetchAllDesignationsInfo();
        setSelectedDesignation(null);
      }
    } catch (err: any) {
      const details = err?.response?.data?.details || "";

      if (
        err?.response?.data?.code === "UNEXPECTED_ERROR" &&
        details.includes(
          "cannot be deleted because it is currently assigned to employees"
        )
      ) {
        toast.warning(
          "Cannot delete: This designation is currently assigned to employees."
        );
      } else {
        console.error("Error deleting designation. Please try again.");
      }
    } finally {
      setDeleteModalVisible(false);
    }
  };

  const columns = [
    {
      title: "S.No",
      key: "serial",
      render: (_: any, __: any, index: number) => index + 1,
    },
    {
      title: "Designation",
      dataIndex: "designation",
    },
    {
      title: "Department",
      dataIndex: "department",
    },
    {
      title: "Total Employees",
      dataIndex: "totalEmployees",
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (text: string) => (
        <span
          className={`badge ${
            text === "active" ? "badge-success" : "badge-danger"
          } d-inline-flex align-items-center badge-xs`}
        >
          <i className="ti ti-point-filled me-1" />
          {text.charAt(0).toUpperCase() + text.slice(1)}
        </span>
      ),
    },
    {
      title: "Actions",
      render: (_: any, record: any) => (
        <div className="action-icon d-inline-flex">
          <button
            className="btn btn-outline-light p-1 me-2" 
            onClick={(e) => {
              if (!canUpdate) {
                toast.warning("You don't have permission to edit designations.");
                e.preventDefault();
                e.stopPropagation();
                return;
              }
              setSelectedDesignation(record);
            }}
            data-bs-toggle={canUpdate ? "modal" : undefined}
            data-bs-target={canUpdate ? "#edit_designation" : undefined}
          >
            <i className="ti ti-edit" />
          </button>
          <button
            className="btn btn-outline-light p-1"
            onClick={(e) => {
              if (!canDelete) {
                toast.warning("You don't have permission to delete designations.");
                e.preventDefault();
                e.stopPropagation();
                return;
              }
              setSelectedDesignation(record);
              setDeleteModalVisible(true);
            }}
          >
            <i className="ti ti-trash" />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
          <div className="my-auto mb-2">
            <h2 className="mb-1">Designations</h2>
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to={all_routes.adminDashboard}>
                    <i className="ti ti-smart-home" />
                  </Link>
                </li>
                <li className="breadcrumb-item active">Designations</li>
              </ol>
            </nav>
          </div>
          <div className="d-flex align-items-center flex-wrap">
            <button
              type="button"
              className="btn btn-primary"  
              onClick={(e) => {
                if (!canCreate) {
                  toast.warning("You don't have permission to create designations.");
                  e.preventDefault();
                  e.stopPropagation();
                  return;
                }
              }}
              data-bs-toggle={canCreate ? "modal" : undefined}
              data-bs-target={canCreate ? "#add_designation" : undefined}
            >
              <i className="ti ti-circle-plus me-2" />
              Add Designation
            </button>
            <div className="ms-2">
              <CollapseHeader />
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body p-0">
            {allInfo.length === 0 ? (
              <p className="p-3">Loading...</p>
            ) : (
              <Table
                key={tableKey}
                dataSource={allInfo.map((item: any) => ({
                  ...item,
                  key: item.designation,
                }))}
                columns={columns}
              />
            )}
          </div>
        </div>
      </div>

      {/* Add Modal */}
      <div className="modal fade" id="add_designation">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h4 className="modal-title">Add Designation</h4>
              <button
                type="button"
                className="btn-close"
                id="add_designation_close"
                data-bs-dismiss="modal"
              />
            </div>
            <Formik
              initialValues={{
                designation_name: "",
                department_name: "",
                status: "active",
              }}
              validationSchema={DesignationSchema}
              onSubmit={handleAdd}
            >
              {({ isSubmitting, errors, touched, resetForm }) => (
                <Form>
                  <div className="modal-body pb-0">
                    <div className="mb-3">
                      <label className="form-label">Designation Name</label>
                      <Field name="designation_name" className="form-control" />
                      {errors.designation_name && touched.designation_name && (
                        <div className="text-danger">
                          {errors.designation_name}
                        </div>
                      )}
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Department Name</label>
                      <Field
                        as="select"
                        name="department_name"
                        className="form-control"
                      >
                        {isDeptLoading ? (
                          <option value="">Loading...</option>
                        ) : (
                          <>
                            <option value="">Select a department</option>
                            {departments.map((dept: any) => (
                              <option key={dept.value} value={dept.value}>
                                {dept.label}
                              </option>
                            ))}
                          </>
                        )}
                      </Field>
                      {errors.department_name && touched.department_name && (
                        <div className="text-danger">
                          {errors.department_name}
                        </div>
                      )}
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Status</label>
                      <Field as="select" name="status" className="form-control">
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                      </Field>
                      {errors.status && touched.status && (
                        <div className="text-danger">{errors.status}</div>
                      )}
                    </div>
                  </div>
                  <div className="modal-footer">
                    <button
                      type="button"
                      className="btn btn-light me-2"
                      onClick={() => resetForm()}
                    >
                      Reset
                    </button>
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={isSubmitting}
                    >
                      Submit
                    </button>
                  </div>
                </Form>
              )}
            </Formik>
          </div>
        </div>
      </div>

      {/* Edit Modal */}
      <div className="modal fade" id="edit_designation">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h4 className="modal-title">Edit Designation</h4>
              <button
                type="button"
                className="btn-close"
                id="edit_designation_close"
                data-bs-dismiss="modal"
              />
            </div>
            <Formik
              enableReinitialize
              initialValues={{
                designation_name: selectedDesignation?.designation || "",
                department_name: selectedDesignation?.department || "",
                status: selectedDesignation?.status || "active",
              }}
              validationSchema={DesignationSchema}
              onSubmit={handleEdit}
            >
              {({ isSubmitting, errors, touched, resetForm }) => (
                <Form>
                  <div className="modal-body pb-0">
                    <div className="mb-3">
                      <label className="form-label">Designation Name</label>
                      <Field name="designation_name" className="form-control" />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Department Name</label>
                      <Field
                        as="select"
                        name="department_name"
                        className="form-control"
                      >
                        {isDeptLoading ? (
                          <option value="">Loading...</option>
                        ) : (
                          <>
                            <option value="">Select a department</option>
                            {departments.map((dept: any) => (
                              <option key={dept.value} value={dept.value}>
                                {dept.label}
                              </option>
                            ))}
                          </>
                        )}
                      </Field>
                      {errors.department_name && touched.department_name && (
                        <div className="text-danger">
                          {errors.department_name as string}
                        </div>
                      )}
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Status</label>
                      <Field as="select" name="status" className="form-control">
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                      </Field>
                    </div>
                  </div>
                  <div className="modal-footer">
                    <button
                      type="button"
                      className="btn btn-light me-2"
                      onClick={() => resetForm()}
                    >
                      Reset
                    </button>
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={isSubmitting}
                    >
                      Save Changes
                    </button>
                  </div>
                </Form>
              )}
            </Formik>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal (AntD) */}
      <Modal
        title="Confirm Deletion"
        open={deleteModalVisible}
        onOk={() => {
          if (!canDelete) {
            toast.warning("You don't have permission to delete designations.");
            return;
          }
          confirmDelete();
        }}
        onCancel={() => setDeleteModalVisible(false)}
        okText="Yes, Delete"
        cancelText="Cancel"
        okButtonProps={{ danger: true }}
      >
        <p>
          Are you sure you want to delete{" "}
          <strong>{selectedDesignation?.designation}</strong>?
        </p>
      </Modal>
    </div>
  );
};

export default Designations;